"use client";

import { useRef, useState, useEffect } from "react";
import { motion, useScroll, useMotionValueEvent } from "motion/react";
import { cn } from "@/lib/utils";
import { DotLottieReact } from "@lottiefiles/dotlottie-react";

interface Frame {
  number: string;
  title: string;
  body: string;
}

const frames: Frame[] = [
  {
    number: "01",
    title: "Semantics",
    body: "The formal semantics of our architecture are ensured across every level of construction. We design systems such that generated programs are provably correct. This is important for the construction of programs that run, and proofs that type check. At every level, from the high level user specification through to learned, latent representations our architecture remains mathematically self-consistent.",
  },
  {
    number: "02",
    title: "Computation",
    body: "We integrate the ability to run arbitrary, recursive computation directly into the model architecture. The ability to execute programs is a fundamental building block of symbolic systems. By leveraging categorical semantics (see 01) we ensure that learned representations are always informed by symbolic execution.",
  },
  {
    number: "03",
    title: "Search",
    body: "Our architecture provides a single, unified representation through which the system can propose a plan, test it (see 02), and ensure its correctness (see 01). Feedback is able to propagate through all of these components uniformly to enable continuous learning and self improvement.",
  },
];

const BORDER_COLOR = "#9a9a9a";
const BORDER_ACTIVE_OPACITY = 1;
const BORDER_INACTIVE_OPACITY = 0.25;
const INACTIVE_WIDTH = 100;

export function ExpandingFrames() {
  return (
    <>
      {/* Mobile layout - shown on small screens, matching homepage card style */}
      <div className="md:hidden mt-16 grid grid-cols-1 gap-4 px-4">
        {frames.map((frame, index) => (
          <MobileCard key={index} frame={frame} index={index} />
        ))}
      </div>

      {/* Desktop layout - shown on md and above */}
      <div className="hidden md:block">
        <DesktopExpandingFrames />
      </div>
    </>
  );
}

function DesktopExpandingFrames() {
  const containerRef = useRef<HTMLDivElement>(null);
  const [activeFrame, setActiveFrame] = useState(0);
  const [isSmallViewport, setIsSmallViewport] = useState(false);

  const { scrollYProgress } = useScroll({
    target: containerRef,
    offset: ["start start", "end end"],
  });

  useMotionValueEvent(scrollYProgress, "change", (latest) => {
    if (latest < 0.33) setActiveFrame(0);
    else if (latest < 0.66) setActiveFrame(1);
    else setActiveFrame(2);
  });

  // Track viewport height changes
  useEffect(() => {
    const checkViewportHeight = () => {
      const height = window.innerHeight;
      setIsSmallViewport(height < 940);
    };

    // Initial check
    checkViewportHeight();

    // Use ResizeObserver to track viewport changes
    const resizeObserver = new ResizeObserver(() => {
      checkViewportHeight();
    });

    // Observe the document element for size changes
    resizeObserver.observe(document.documentElement);

    // Also listen to window resize as fallback
    window.addEventListener("resize", checkViewportHeight);

    return () => {
      resizeObserver.disconnect();
      window.removeEventListener("resize", checkViewportHeight);
    };
  }, []);

  const handleFrameClick = (index: number) => {
    setActiveFrame(index);
  };

  return (
    <div ref={containerRef} className="relative h-[300vh]">
      <div className="sticky top-0 h-screen flex items-center">
        {/* Frames Container - Single outer border */}
        <div
          className="relative h-[70vh] max-h-[700px] min-h-[500px] border border-solid w-[calc(100%-4rem)] lg:w-[calc(100%-12rem)] max-w-[102rem] mx-auto"
          style={{
            borderColor: `rgba(154, 154, 154, ${BORDER_ACTIVE_OPACITY})`,
          }}
        >
          {/* Frame 1 - Root */}
          <div
            className="absolute inset-0 bg-[#FFFDF0] cursor-pointer overflow-hidden flex"
            onClick={() => handleFrameClick(0)}
          >
            {/* Frame 1 Content */}
            <FrameContent
              frame={frames[0]!}
              isActive={activeFrame === 0}
              index={0}
              isSmallViewport={isSmallViewport}
            />

            {/* Frame 2 - Nested in Frame 1 */}
            <motion.div
              className="absolute bg-[#FFFDF0] cursor-pointer overflow-hidden flex"
              style={{
                borderColor: BORDER_COLOR,
                borderStyle: "solid",
                borderLeftWidth: 1,
                borderTopWidth: 0,
                borderBottomWidth: 0,
                borderRightWidth: 0,
                borderLeftColor: `rgba(154, 154, 154, ${BORDER_INACTIVE_OPACITY})`,
                zIndex: 10,
                top: 0,
                bottom: 0,
                right: 0,
              }}
              onClick={(e) => {
                e.stopPropagation();
                handleFrameClick(1);
              }}
              initial={false}
              animate={{
                left:
                  activeFrame >= 1
                    ? INACTIVE_WIDTH
                    : `calc(100% - ${INACTIVE_WIDTH * 2}px)`,
                borderLeftWidth: 1,
              }}
              transition={{ duration: 0.5, ease: [0.16, 1, 0.3, 1] }}
            >
              <FrameContent
                frame={frames[1]!}
                isActive={activeFrame === 1}
                index={1}
                isSmallViewport={isSmallViewport}
              />

              {/* Frame 3 - Nested in Frame 2 */}
              <motion.div
                className="absolute bg-[#FFFDF0] cursor-pointer overflow-hidden flex"
                style={{
                  borderColor: BORDER_COLOR,
                  borderStyle: "solid",
                  borderLeftWidth: 1,
                  borderTopWidth: 0,
                  borderBottomWidth: 0,
                  borderRightWidth: 0,
                  borderLeftColor: `rgba(154, 154, 154, ${BORDER_INACTIVE_OPACITY})`,
                  zIndex: 20,
                  top: 0,
                  bottom: 0,
                  right: 0,
                }}
                onClick={(e) => {
                  e.stopPropagation();
                  handleFrameClick(2);
                }}
                initial={false}
                animate={{
                  left:
                    activeFrame >= 2
                      ? INACTIVE_WIDTH
                      : `calc(100% - ${INACTIVE_WIDTH}px)`,
                  borderLeftWidth: 1,
                }}
                transition={{ duration: 0.5, ease: [0.16, 1, 0.3, 1] }}
              >
                <FrameContent
                  frame={frames[2]!}
                  isActive={activeFrame === 2}
                  index={2}
                  isSmallViewport={isSmallViewport}
                />
              </motion.div>
            </motion.div>
          </div>
        </div>
      </div>
    </div>
  );
}

function MobileCard({ frame, index }: { frame: Frame; index: number }) {
  const [isInView, setIsInView] = useState(false);
  const cardRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        setIsInView(entry?.isIntersecting ?? false);
      },
      { rootMargin: "100px" }
    );

    if (cardRef.current) {
      observer.observe(cardRef.current);
    }

    return () => observer.disconnect();
  }, []);

  return (
    <div
      ref={cardRef}
      className="relative flex flex-col p-8 min-h-[350px] border border-black/10 bg-[#FFFDF0] overflow-hidden mix-blend-darken"
    >
      {/* Number */}
      <div className="mb-4">
        <span className="font-ibm-plex-mono text-[22px] text-periwinkle font-medium">
          {frame.number}
        </span>
      </div>
      {/* Lottie visualization */}
      <div
        className={`w-[250px] h-[250px] mb-6 transition-opacity duration-700 delay-1000 ${isInView ? "opacity-100" : "opacity-0"}`}
      >
        {isInView && (
          <DotLottieReact
            src={`/lottie/sym-lottie-${index + 1}.lottie`}
            autoplay
            loop
            className="w-full h-full"
            renderConfig={{ autoResize: true, devicePixelRatio: 2 }}
          />
        )}
      </div>
      {/* Content */}
      <div>
        <h3 className="font-arizona-mix text-[22px] leading-[1.3] font-medium text-[#0a0a0a] tracking-[-0.5px] text-pretty">
          {frame.title}
        </h3>
        <p className="font-arizona-sans text-[16px] text-[#0a0a0a] mt-4 leading-relaxed">
          {frame.body}
        </p>
      </div>
    </div>
  );
}

function FrameContent({
  frame,
  isActive,
  index,
  isSmallViewport,
}: {
  frame: Frame;
  isActive: boolean;
  index: number;
  isSmallViewport: boolean;
}) {
  const lottieSize = isSmallViewport ? 125 : 250;
  return (
    <div className="p-6 flex flex-col mix-blend-darken relative">
      {/* Frame Number - Always visible, even in collapsed state */}
      <div className="mb-4">
        <span
          className={cn(
            "font-ibm-plex-mono text-3xl transition-colors duration-200",
            isActive ? "text-periwinkle" : "text-[#9a9a9a]"
          )}
        >
          {frame.number}
        </span>
      </div>

      {/* Animated content that fades in/out */}
      <motion.div
        animate={{
          opacity: isActive ? 1 : 0,
        }}
        transition={{
          duration: isActive ? 0.5 : 0.15,
          delay: isActive ? 0.15 : 0,
        }}
        style={{ pointerEvents: isActive ? "auto" : "none" }}
      >
        {/* Lottie visualization - only mount when active */}
        <div
          className="mb-6 transition-all duration-300"
          style={{ width: `${lottieSize}px`, height: `${lottieSize}px` }}
        >
          {isActive && (
            <DotLottieReact
              src={`/lottie/sym-lottie-${index + 1}.lottie`}
              autoplay
              loop
              className="w-full h-full"
              renderConfig={{ autoResize: true, devicePixelRatio: 2 }}
            />
          )}
        </div>

        {/* Content Area */}
        <div className="max-w-[420px]">
          <p className="font-arizona-mix text-[28px] leading-[1.2] font-medium text-[#0a0a0a] tracking-[-1.5] text-pretty">
            {frame.title}
          </p>
          <p className="font-arizona-sans text-[16px] text-[#0a0a0a] mt-4 leading-relaxed">
            {frame.body}
          </p>
        </div>
      </motion.div>
    </div>
  );
}
